import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { microBrand } from './brand';
import './navigation-underline.scss';
/** Keep legacy flat detail routes (users-edit-user, edit-placements) in their section. */
export function isNavigationActive(pathname, path) {
    const current = pathname.toLowerCase().replace(/\/+$/, '') || '/';
    const target = `/${path.replace(/^\/+|\/+$/g, '')}`.toLowerCase();
    return current === target || current.startsWith(`${target}/`) || current.startsWith(`${target}-`)
        || (target === '/placements' && current === '/edit-placements');
}
/** Product-local navigation presentation; links and router history remain immediate. */
export function NavigationUnderline({ items, beforeNavigate }) {
    const { pathname } = useLocation();
    const navRef = useRef(null);
    const indicatorRef = useRef(null);
    const itemKey = items.map(item => `${item.path}:${!!item.disabled}`).join('|');
    useLayoutEffect(() => {
        const nav = navRef.current;
        const indicator = indicatorRef.current;
        if (!nav || !indicator)
            return;
        let disposed = false;
        let frame;
        const measure = () => {
            if (disposed)
                return;
            const active = nav.querySelector('[aria-current="page"]');
            if (!active || !nav.getBoundingClientRect().width) {
                indicator.hidden = true;
                return;
            }
            const rect = active.getBoundingClientRect();
            const container = nav.getBoundingClientRect();
            indicator.hidden = false;
            indicator.style.width = `${rect.width}px`;
            indicator.style.transform = `translateX(${rect.left - container.left}px)`;
        };
        const schedule = () => {
            if (frame !== undefined)
                cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => { frame = undefined; measure(); });
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule);
        observer?.observe(nav);
        nav.querySelectorAll('a').forEach(link => observer?.observe(link));
        window.addEventListener('resize', schedule);
        document.fonts?.addEventListener('loadingdone', schedule);
        document.fonts?.ready.then(() => { if (!disposed)
            schedule(); });
        return () => {
            disposed = true;
            if (frame !== undefined)
                cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener('resize', schedule);
            document.fonts?.removeEventListener('loadingdone', schedule);
        };
    }, [pathname, itemKey]);
    return (_jsxs("nav", { ref: navRef, "aria-label": "Main navigation", className: "portal-navigation", style: {
            '--navigation-mark': microBrand.accentMark,
            '--navigation-focus': microBrand.focus,
            '--navigation-tint': microBrand.tint
        }, children: [items.map(item => item.disabled ? (_jsx("span", { "aria-disabled": "true", className: "portal-navigation__link", children: item.label }, item.path)) : (_jsx(Link, { to: `/${item.path.replace(/^\/+/, '')}`, onClick: event => {
                    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                        return;
                    if (beforeNavigate && !beforeNavigate(`/${item.path.replace(/^\/+/, '')}`))
                        event.preventDefault();
                }, className: "portal-navigation__link", "aria-current": isNavigationActive(pathname, item.path) ? 'page' : undefined, children: item.label }, item.path))), _jsx("span", { ref: indicatorRef, className: "portal-navigation__underline", "aria-hidden": "true", hidden: true })] }));
}
